import { useIntl } from 'dibs-react-intl';
import { FC, useRef, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import { messages } from '../../messages/pdpDealerDetailsMessages';
import {
    MAKE_OFFER,
    CONTACT_SELLER,
} from '../../PdpSharedContactTabsVariant/PdpSharedSellerTabsContants';
import { trackSellerDetailsView } from '../../tracking/pdpTracking';

// components
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { PdpSharedSellerInfo } from '../../shared/PdpSharedSellerInfo/PdpSharedSellerInfo';
import { ContactDealerInlineFormLoader } from 'dibs-buyer-layout/exports/ContactDealerInlineFormLoader';
import { PdpSharedSellerDetailsBio } from '../../shared/PdpSharedSellerDetails/PdpSharedSellerDetailsBio';
import { PdpSharedSellerDetailsHeader } from '../../shared/PdpSharedSellerDetails/PdpSharedSellerDetailsHeader';
import { PdpSharedSellerDistinction } from '../../shared/PdpSharedSellerDistinction/PdpSharedSellerDistinction';

// styles
import styles from './PdpRespSharedSellerDetails.scss';

//types
import { PdpRespSharedSellerDetails_sku$key as Sku } from './__generated__/PdpRespSharedSellerDetails_sku.graphql';
import { PdpRespSharedSellerDetails_item$key as Item } from './__generated__/PdpRespSharedSellerDetails_item.graphql';
import { PdpRespSharedSellerDetails_user$key as User } from './__generated__/PdpRespSharedSellerDetails_user.graphql';
import { PdpRespSharedSellerDetails_viewer$key as Viewer } from './__generated__/PdpRespSharedSellerDetails_viewer.graphql';

// lazy components
const PdpSharedSellerTabs = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedContactTabs" */ '../../shared/PdpSharedSellerTabs/PdpSharedSellerTabs'
        )
);

const PdpSharedCallDealerCta = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedCallDealerCta" */ '../../PdpSharedCallDealerCta/PdpSharedCallDealerCta'
        )
);

const PdpSharedSellerDetailsReviewContainer = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedSellerDetailsReviewContainer" */ '../../shared/PdpSharedSellerDetails/PdpSharedSellerDetailsReviewContainer'
        )
);

const viewerFragment = graphql`
    fragment PdpRespSharedSellerDetails_viewer on Viewer {
        ...ContactDealerInlineForm_viewer
        ...PdpSharedSellerTabs_viewer @include(if: $afterDisplay)
        ...PdpSharedSellerDetailsReviewContainer_viewer @include(if: $afterDisplay)
    }
`;
const itemFragment = graphql`
    fragment PdpRespSharedSellerDetails_item on Item {
        dealerDetailsDisplayInfo: displayInfo {
            ctas {
                showMakeOffer
            }
        }
        seller {
            serviceId
            isAccessible
            liveStorefrontProfile {
                aboutUsUnbranded
            }
            isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
            ...PdpSharedSellerInfo_seller
            ...PdpSharedSellerDetailsBio_seller
            ...PdpSharedSellerDistinction_seller
            ...PdpSharedSellerDetailsHeader_seller
            ...PdpSharedSellerDetailsReviewContainer_seller @include(if: $afterDisplay)
        }
        ...PdpSharedCallDealerCta_item
        ...ContactDealerInlineForm_item
        ...PdpSharedSellerTabs_item @include(if: $afterDisplay)
    }
`;
const userFragment = graphql`
    fragment PdpRespSharedSellerDetails_user on User {
        isVerifiedTrade
        ...PdpSharedCallDealerCta_user
        ...ContactDealerInlineForm_user
        ...PdpSharedSellerDetailsHeader_user
        ...PdpSharedSellerTabs_user @include(if: $afterDisplay)
    }
`;
const skuFragment = graphql`
    fragment PdpRespSharedSellerDetails_sku on ItemSkuType {
        ...PdpSharedSellerTabs_sku
    }
`;

export const PdpRespSharedSellerDetails: FC<{
    item: Item;
    viewer: Viewer;
    sku: Sku | null;
    dataTn?: string;
    currency: string;
    user: User | null;
    loadForms?: boolean;
}> = ({
    dataTn,
    currency,
    sku: skuRef,
    item: itemRef,
    user: userRef,
    viewer: viewerRef,
    loadForms: loadFormsRef,
}) => {
    const intl = useIntl();
    const sellerDetailsRef = useRef(null);
    const sku = useFragment(skuFragment, skuRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const [loadForms, setLoadForms] = useState(loadFormsRef || false);

    const { seller, dealerDetailsDisplayInfo } = item;
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const aboutUs = liveStorefrontProfile?.aboutUsUnbranded;
    const isNegotiable = dealerDetailsDisplayInfo?.ctas?.showMakeOffer || false;

    if (!seller?.isAccessible) {
        return null;
    }

    const header =
        isRewardsTier2 && aboutUs ? (
            <>
                <div className={styles.detailsHeaderBio} data-tn="pdp-dealer-details-header-bio">
                    <div className={styles.leftSection}>
                        <PdpSharedSellerDetailsHeader user={user} seller={seller} />
                    </div>
                    <div className={`${styles.detailsBio} ${styles.rightSection}`}>
                        <PdpSharedSellerDetailsBio seller={seller} />
                    </div>
                </div>
                <div className={`${styles.detailsStatsContainer} ${styles.leftSection}`}>
                    <div className={styles.detailsSellerStats}>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedSellerDistinction seller={seller} />
                        </div>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedSellerInfo seller={seller} />
                        </div>
                    </div>
                    <ClientSuspense fallback={null}>
                        <PdpSharedSellerDetailsReviewContainer viewer={viewer} seller={seller} />
                    </ClientSuspense>
                </div>
            </>
        ) : (
            <div className={`${styles.detailsContainer} ${styles.leftSection}`}>
                <div className={styles.detailsHeader} data-tn="pdp-dealer-info">
                    <PdpSharedSellerDetailsHeader seller={seller} user={user} />
                </div>
                <div className={styles.detailsSellerStats}>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedSellerDistinction seller={seller} />
                    </div>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedSellerInfo seller={seller} />
                    </div>
                </div>
                <ClientSuspense fallback={null}>
                    <PdpSharedSellerDetailsReviewContainer viewer={viewer} seller={seller} />
                </ClientSuspense>
            </div>
        );

    return (
        <>
            <div className={styles.wrapper} ref={sellerDetailsRef} data-tn={dataTn}>
                <VisibilityTracker
                    elementRef={sellerDetailsRef}
                    onVisibilityChange={({ isVisible }) => {
                        setLoadForms(isVisible);
                        trackSellerDetailsView(isNegotiable, isVisible);
                    }}
                />
                {header}

                <div
                    className={`${styles.formContainer} ${styles.rightSection}`}
                    data-tn="pdp-contact-dealer-info"
                >
                    {isNegotiable && loadForms ? (
                        <ClientSuspense fallback={null}>
                            <PdpSharedSellerTabs
                                sku={sku}
                                item={item}
                                user={user}
                                viewer={viewer}
                                buttonProps={{ size: 'medium' }}
                                currency={currency}
                                tabOrder={
                                    user?.isVerifiedTrade
                                        ? [CONTACT_SELLER, MAKE_OFFER]
                                        : [MAKE_OFFER, CONTACT_SELLER]
                                }
                            />
                        </ClientSuspense>
                    ) : (
                        <ContactDealerInlineFormLoader
                            viewer={viewer}
                            load={!isNegotiable && loadForms}
                            item={item}
                            user={user}
                            responseTimePos="none"
                            inputProps={{
                                hasAnimatedPlaceholder: true,
                                placeholder: intl.formatMessage(messages.namePlaceholder),
                            }}
                            buttonProps={{ className: styles.contactDealerButton }}
                        />
                    )}
                    {!isNegotiable && loadForms && (
                        <ClientSuspense fallback={null}>
                            <PdpSharedCallDealerCta item={item} user={user} />
                        </ClientSuspense>
                    )}
                </div>
            </div>
        </>
    );
};

// lazy load
export default PdpRespSharedSellerDetails;
